import { ATTRIBUTION_MODEL_LABELS, ATTRIBUTION_MODELS, type AttributionModel } from '@/api/firstPartyTraffic'
import { GA4_GROUP_BY, GA4_GROUP_BY_LABELS, type Ga4GroupBy } from '@/api/types'
import { SegmentedControl } from '@/design'
import type { Comparison } from '@/lib/filters'

import type { TrafficSource } from './firstPartyQuery'

const SOURCE_OPTIONS: readonly { value: TrafficSource; label: string }[] = [
  { value: 'ga4', label: 'Google Analytics 4' },
  { value: 'tmt', label: 'TmT first-party' },
]

/** Whose numbers the page shows: GA4's, as reported, or the website's own, as TmT counts them. Never both in one table. */
export function SourceSwitch({ value, onChange }: { value: TrafficSource; onChange: (next: TrafficSource) => void }) {
  return <SegmentedControl label="Data source" options={SOURCE_OPTIONS} value={value} onChange={onChange} />
}

/** The attribution model the first-party orders are credited under (blueprint 05 §5.8). */
export function ModelSelect({ value, onChange }: { value: AttributionModel; onChange: (next: AttributionModel) => void }) {
  return (
    <label className="ci-field ci-field--inline">
      <span>Attribution model</span>
      <select value={value} onChange={(event) => onChange(event.target.value as AttributionModel)}>
        {ATTRIBUTION_MODELS.map((option) => (
          <option key={option} value={option}>
            {ATTRIBUTION_MODEL_LABELS[option]}
          </option>
        ))}
      </select>
    </label>
  )
}

/** The grouping selector; the date range and comparison are the global filter bar's. */
export function GroupBySelect({ value, onChange }: { value: Ga4GroupBy; onChange: (next: Ga4GroupBy) => void }) {
  return (
    <label className="ci-field ci-field--inline">
      <span>Group by</span>
      <select value={value} onChange={(event) => onChange(event.target.value as Ga4GroupBy)}>
        {GA4_GROUP_BY.map((option) => (
          <option key={option} value={option}>
            {GA4_GROUP_BY_LABELS[option]}
          </option>
        ))}
      </select>
    </label>
  )
}

/** GA4 traffic compares with the previous period only; say so when the filter bar asks for more. */
export function ComparisonNote({ comparison }: { comparison: Comparison }) {
  if (comparison !== 'previous_year') return null
  return (
    <p role="note" className="tr-note">
      Google Analytics traffic compares with the previous period only, so no comparison is shown for "same period last year".
    </p>
  )
}
